<template>
  <div class="admin-reviews mx-lg-4 mx-1">
    <!-- Header -->
    <div class="reviews-header">
      <div>
        <h2 class="page-title">Gestión de Reseñas</h2>
        <p class="page-subtitle">Administra las reseñas de productos pendientes de aprobación</p>
      </div>
      <div class="header-stats">
        <span class="pending-badge">
          {{ pendingReviews.length }} Pendientes
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando reseñas...</span>
      </div>
      <p class="text-muted mt-3">Cargando reseñas pendientes...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ error }}
      <button @click="loadPendingReviews" class="btn btn-retry btn-sm ms-3">
        <i class="fas fa-sync me-1"></i>
        Reintentar
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="pendingReviews.length === 0" class="empty-state text-center py-5">
      <i class="fas fa-check-circle fa-4x text-success mb-3"></i>
      <h4 class="text-white">¡Todo al día!</h4>
      <p class="text-muted">No hay reseñas pendientes de aprobación</p>
      <button @click="loadPendingReviews" class="btn btn-refresh">
        <i class="fas fa-sync me-2"></i>
        Actualizar
      </button>
    </div>

    <!-- Reviews List -->
    <div v-else class="reviews-list">
      <!-- Filter and Actions Bar -->
      <div class="actions-bar d-flex justify-content-between align-items-center mb-4">
        <div class="filter-controls">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control search-input"
            placeholder="Buscar por producto o usuario..."
          >
        </div>
        <div class="bulk-actions">
          <button
            @click="loadPendingReviews"
            class="btn btn-refresh btn-sm"
            :disabled="isLoading"
          >
            <i class="fas fa-sync me-1" :class="{ 'fa-spin': isLoading }"></i>
            Actualizar
          </button>
        </div>
      </div>

      <!-- Reviews Cards -->
      <div class="row">
        <div
          v-for="review in filteredReviews"
          :key="review.id"
          class="col-lg-6 col-xl-4 mb-4"
        >
          <div class="review-card h-100">
            <div class="review-card-header">
              <div class="review-meta">
                <h6 class="review-username">
                  <i class="fas fa-user-circle me-2"></i>
                  {{ review.username }}
                </h6>
                <small class="review-date">
                  {{ formatDate(review.created_at) }}
                </small>
              </div>
              <div class="rating-display">
                <div class="stars">
                  <i
                    v-for="star in 5"
                    :key="star"
                    class="fas fa-star"
                    :class="star <= review.rating ? 'star-active' : 'star-inactive'"
                  ></i>
                </div>
                <span class="rating-text">({{ review.rating }}/5)</span>
              </div>
            </div>

            <div class="review-card-body">
              <!-- Product Info -->
              <div class="product-info">
                <h6 class="product-name">
                  <i class="fas fa-box me-2"></i>
                  {{ review.product_name }}
                </h6>
              </div>

              <!-- Review Content -->
              <div class="review-content">
                <p class="review-text" :class="{ 'text-truncated': review.comment.length > 150 }">
                  {{ review.comment }}
                </p>
                <button
                  v-if="review.comment.length > 150"
                  @click="toggleFullComment(review.id)"
                  class="btn btn-link btn-sm p-0 text-primary"
                >
                  {{ expandedComments.includes(review.id) ? 'Ver menos' : 'Ver más' }}
                </button>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="review-card-footer">
              <div class="d-flex gap-2">
                <button
                  @click="approveReviewAction(review)"
                  class="btn btn-approve btn-sm flex-fill"
                  :disabled="processingReviews.includes(review.id)"
                >
                  <i
                    v-if="processingReviews.includes(review.id)"
                    class="fas fa-spinner fa-spin me-1"
                  ></i>
                  <i v-else class="fas fa-check me-1"></i>
                  Aprobar
                </button>
                <button
                  @click="rejectReviewAction(review)"
                  class="btn btn-reject btn-sm flex-fill"
                  :disabled="processingReviews.includes(review.id)"
                >
                  <i
                    v-if="processingReviews.includes(review.id)"
                    class="fas fa-spinner fa-spin me-1"
                  ></i>
                  <i v-else class="fas fa-times me-1"></i>
                  Rechazar
                </button>
                <button
                  @click="deleteReviewAction(review)"
                  class="btn btn-delete btn-sm"
                  :disabled="processingReviews.includes(review.id)"
                >
                  <i
                    v-if="processingReviews.includes(review.id)"
                    class="fas fa-spinner fa-spin"
                  ></i>
                  <i v-else class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0,0,0,0.8);"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-exclamation-triangle text-warning me-2"></i>
              Confirmar Eliminación
            </h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              @click="cancelDelete"
            ></button>
          </div>
          <div class="modal-body">
            <p>
              ¿Estás seguro de que deseas eliminar esta reseña de <strong>{{ reviewToDelete?.username }}</strong>?
            </p>
            <p class="modal-warning">
              Esta acción no se puede deshacer.
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-cancel"
              @click="cancelDelete"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn btn-confirm-delete"
              @click="confirmDelete"
              :disabled="processingReviews.includes(reviewToDelete?.id)"
            >
              <i
                v-if="processingReviews.includes(reviewToDelete?.id)"
                class="fas fa-spinner fa-spin me-1"
              ></i>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { 
  getPendingReviews, 
  approveReview, 
  rejectReview, 
  deleteReview 
} from '@/services/products';

const toast = useToast();

// State
const pendingReviews = ref([]);
const isLoading = ref(false);
const error = ref(null);
const searchQuery = ref('');
const expandedComments = ref([]);
const processingReviews = ref([]);
const showDeleteModal = ref(false);
const reviewToDelete = ref(null);

// Computed
const filteredReviews = computed(() => {
  if (!searchQuery.value.trim()) return pendingReviews.value;
  
  const query = searchQuery.value.toLowerCase();
  return pendingReviews.value.filter(review => 
    review.username.toLowerCase().includes(query) ||
    review.product_name.toLowerCase().includes(query) ||
    review.comment.toLowerCase().includes(query)
  );
});

// Methods
const loadPendingReviews = async () => {
  isLoading.value = true;
  error.value = null;
  
  try {
    const reviews = await getPendingReviews();
    pendingReviews.value = reviews || [];
  } catch (err) {
    console.error('Error loading pending reviews:', err);
    error.value = err.message || 'Error al cargar las reseñas pendientes';
  } finally {
    isLoading.value = false;
  }
};

const approveReviewAction = async (review) => {
  if (processingReviews.value.includes(review.id)) return;
  
  processingReviews.value.push(review.id);
  
  try {
    await approveReview(review.id);
    toast.success(`Reseña de ${review.username} aprobada exitosamente`);
    
    // Remove from pending list
    pendingReviews.value = pendingReviews.value.filter(r => r.id !== review.id);
  } catch (error) {
    console.error('Error approving review:', error);
    toast.error(error.message || 'Error al aprobar la reseña');
  } finally {
    processingReviews.value = processingReviews.value.filter(id => id !== review.id);
  }
};

const rejectReviewAction = async (review) => {
  if (processingReviews.value.includes(review.id)) return;
  
  processingReviews.value.push(review.id);
  
  try {
    await rejectReview(review.id);
    toast.warning(`Reseña de ${review.username} rechazada`);
    
    // Remove from pending list
    pendingReviews.value = pendingReviews.value.filter(r => r.id !== review.id);
  } catch (error) {
    console.error('Error rejecting review:', error);
    toast.error(error.message || 'Error al rechazar la reseña');
  } finally {
    processingReviews.value = processingReviews.value.filter(id => id !== review.id);
  }
};

const deleteReviewAction = (review) => {
  reviewToDelete.value = review;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  if (!reviewToDelete.value || processingReviews.value.includes(reviewToDelete.value.id)) return;
  
  const review = reviewToDelete.value;
  processingReviews.value.push(review.id);
  
  try {
    await deleteReview(review.id);
    toast.success(`Reseña de ${review.username} eliminada exitosamente`);
    
    // Remove from pending list
    pendingReviews.value = pendingReviews.value.filter(r => r.id !== review.id);
    
    cancelDelete();
  } catch (error) {
    console.error('Error deleting review:', error);
    toast.error(error.message || 'Error al eliminar la reseña');
  } finally {
    processingReviews.value = processingReviews.value.filter(id => id !== review.id);
  }
};

const cancelDelete = () => {
  showDeleteModal.value = false;
  reviewToDelete.value = null;
};

const toggleFullComment = (reviewId) => {
  const index = expandedComments.value.indexOf(reviewId);
  if (index > -1) {
    expandedComments.value.splice(index, 1);
  } else {
    expandedComments.value.push(reviewId);
  }
};

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'Fecha no disponible';
  }
};

// Initialize
onMounted(() => {
  loadPendingReviews();
});
</script>

<style scoped>
/* ===== Container ===== */
.admin-reviews {
  background: rgba(0,0,0,0.15);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  padding: 1.5rem;
}

/* ===== Header ===== */
.reviews-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #ccc;
  margin: 0;
  letter-spacing: -0.01em;
}

.page-subtitle {
  color: #555;
  font-size: 0.82rem;
  margin: 0.25rem 0 0;
}

.pending-badge {
  background: rgba(251,191,36,0.15);
  color: #fbbf24;
  border: 1px solid rgba(251,191,36,0.25);
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
}

/* ===== Loading ===== */
.spinner-border.text-primary { color: var(--primary-color) !important; }

/* ===== Error State ===== */
.alert-danger {
  background: rgba(248,113,113,0.08);
  border: 1px solid rgba(248,113,113,0.15);
  color: #f87171;
  border-radius: 8px;
  font-size: 0.84rem;
}

.btn-retry {
  background: rgba(248,113,113,0.15);
  border: 1px solid rgba(248,113,113,0.25);
  color: #f87171;
  font-size: 0.78rem;
  border-radius: 6px;
  transition: all 0.15s ease;
}
.btn-retry:hover { background: rgba(248,113,113,0.25); color: #f87171; }

/* ===== Empty State ===== */
.empty-state {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  margin: 1rem 0;
}
.empty-state i.fa-check-circle { color: var(--primary-color) !important; }
.empty-state h4 { color: #ccc; font-size: 1.05rem; }
.empty-state p { color: #555; font-size: 0.84rem; }

.btn-refresh {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
  font-size: 0.82rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.btn-refresh:hover { color: #ccc; border-color: rgba(255,255,255,0.15); background: rgba(255,255,255,0.04); }

/* ===== Actions Bar ===== */
.actions-bar {
  background: rgba(0,0,0,0.2);
  padding: 0.85rem;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.06);
}

.search-input {
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
  color: #ccc;
  font-size: 0.84rem;
  border-radius: 8px;
  width: 300px;
  transition: all 0.15s ease;
}
.search-input::placeholder { color: #444; }
.search-input:focus {
  background: rgba(0,0,0,0.35);
  border-color: rgba(var(--primary-color-rgb), 0.4);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.15);
  color: #ddd;
}

/* ===== Review Cards ===== */
.review-card {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  transition: border-color 0.15s ease;
  display: flex;
  flex-direction: column;
}
.review-card:hover {
  border-color: rgba(255,255,255,0.1);
}

.review-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}

.review-username {
  color: #ccc;
  font-size: 0.88rem;
  font-weight: 600;
  margin: 0 0 0.15rem;
}
.review-username i { color: #666; }

.review-date { color: #555; font-size: 0.75rem; }

.rating-display .stars { font-size: 0.82rem; }
.rating-display .stars .fa-star { margin-right: 2px; }
.star-active { color: #fbbf24; }
.star-inactive { color: #333; }
.rating-text { color: #555; font-size: 0.72rem; margin-left: 0.25rem; }

/* Card Body */
.review-card-body {
  padding: 1rem;
  flex: 1;
}

.product-info { margin-bottom: 0.75rem; }
.product-name {
  color: var(--primary-color);
  font-size: 0.84rem;
  font-weight: 600;
  margin: 0;
}

.review-content { line-height: 1.6; }
.review-text { color: #aaa; font-size: 0.84rem; margin: 0; }

.review-content .text-truncated {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.btn-link.text-primary {
  color: var(--primary-color) !important;
  font-size: 0.78rem;
  text-decoration: none;
}
.btn-link.text-primary:hover { color: var(--primary-light) !important; }

/* Card Footer */
.review-card-footer {
  padding: 0.75rem 1rem;
  border-top: 1px solid rgba(255,255,255,0.04);
}

.btn-approve {
  background: rgba(var(--primary-color-rgb), 0.2);
  border: 1px solid rgba(var(--primary-color-rgb), 0.3);
  color: var(--primary-color);
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 6px;
  transition: all 0.15s ease;
}
.btn-approve:hover:not(:disabled) { background: rgba(var(--primary-color-rgb), 0.3); color: var(--primary-light); }
.btn-approve:disabled { opacity: 0.5; }

.btn-reject {
  background: rgba(251,191,36,0.12);
  border: 1px solid rgba(251,191,36,0.25);
  color: #fbbf24;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 6px;
  transition: all 0.15s ease;
}
.btn-reject:hover:not(:disabled) { background: rgba(251,191,36,0.2); color: #fcd34d; }
.btn-reject:disabled { opacity: 0.5; }

.btn-delete {
  background: rgba(248,113,113,0.12);
  border: 1px solid rgba(248,113,113,0.25);
  color: #f87171;
  font-size: 0.8rem;
  border-radius: 6px;
  transition: all 0.15s ease;
}
.btn-delete:hover:not(:disabled) { background: rgba(248,113,113,0.2); color: #fca5a5; }
.btn-delete:disabled { opacity: 0.5; }

/* ===== Modal ===== */
.modal.fade.show {
  background-color: rgba(0,0,0,0.6) !important;
}

.modal-content {
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  color: #aaa;
  border-radius: 10px;
  animation: modal-appear 0.2s ease-out;
}

.modal-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.modal-title { color: #ccc; font-size: 0.95rem; font-weight: 600; }
.modal-title .text-warning { color: #fbbf24 !important; }

.modal-body { padding: 1.25rem; }
.modal-body p { color: #aaa; font-size: 0.84rem; }
.modal-body strong { color: #ccc; }
.modal-warning { color: #555 !important; font-size: 0.78rem !important; }

.modal-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid rgba(255,255,255,0.06);
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.btn-close { filter: invert(0.5); }
.btn-close:hover { filter: invert(0.8); }

.btn-cancel {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
  border-radius: 8px;
  font-size: 0.84rem;
  padding: 0.45rem 0.85rem;
  transition: all 0.15s ease;
}
.btn-cancel:hover { color: #ccc; border-color: rgba(255,255,255,0.15); }

.btn-confirm-delete {
  background: rgba(248,113,113,0.2);
  border: 1px solid rgba(248,113,113,0.3);
  color: #f87171;
  border-radius: 8px;
  font-size: 0.84rem;
  font-weight: 600;
  padding: 0.45rem 0.85rem;
  transition: all 0.15s ease;
}
.btn-confirm-delete:hover:not(:disabled) { background: rgba(248,113,113,0.3); }
.btn-confirm-delete:disabled { opacity: 0.5; }

@keyframes modal-appear {
  from { opacity: 0; transform: translateY(-12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .admin-reviews { padding: 1rem; }
  .reviews-header { flex-direction: column; align-items: flex-start; gap: 0.75rem; }
  .actions-bar { flex-direction: column; gap: 0.75rem; align-items: stretch !important; }
  .search-input { width: 100% !important; }
  .review-card-footer .d-flex { flex-direction: column; gap: 0.4rem; }
  .review-card-footer .btn { flex: none !important; }
}

@media (max-width: 576px) {
  .admin-reviews { padding: 0.75rem; }
  .page-title { font-size: 1.1rem; }
  .header-stats { width: 100%; }
  .pending-badge { width: 100%; text-align: center; display: block; }
}
</style>
